import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireExpert?: boolean;
  requireAdvanced?: boolean;
  requireSuperuser?: boolean;
  requireSecretCorner?: boolean;
}

const LockIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
    />
  </svg>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireExpert = false,
  requireAdvanced = false,
  requireSuperuser = false,
  requireSecretCorner = false
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-ink mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login with return path
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check superuser requirement. Unlike the two below, is_superuser is not
  // self-satisfying -- there is no wider role that implies it.
  if (requireSuperuser && !user?.is_superuser) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-paper rounded-brand-lg shadow-raised border border-line p-8">
            <LockIcon className="w-10 h-10 text-ink-3 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-4">Administrators only</h2>
            <p className="text-ink-2 mb-6">
              This area requires an administrator account. There's no request process for it —
              contact your administrator directly if you need something done here.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-green-ink text-white rounded-lg hover:opacity-90"
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Check expert access requirement
  if (requireExpert && !user?.is_expert && !user?.is_superuser) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-paper rounded-brand-lg shadow-raised border border-line p-8">
            <LockIcon className="w-10 h-10 text-ink-3 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-4">Expert access required</h2>
            <p className="text-ink-2 mb-6">
              This area is restricted to expert users. There's no self-service request for expert
              access — if you need it, reach out to your administrator.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-green-ink text-white rounded-lg hover:opacity-90"
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Check advanced access requirement
  if (requireAdvanced && !user?.is_advanced_user && !user?.is_superuser) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-paper rounded-brand-lg shadow-raised border border-line p-8">
            <LockIcon className="w-10 h-10 text-ink-3 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-4">Advanced access required</h2>
            <p className="text-ink-2 mb-6">
              You don't have advanced access yet. Head back to the home screen and choose
              "Request Advanced Access" — it's a real request to your administrator, not just a
              form field.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-green-ink text-white rounded-lg hover:opacity-90"
            >
              Go request access
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Check Secret Corner pass requirement
  if (requireSecretCorner && !user?.is_secret_corner && !user?.is_superuser) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-paper rounded-brand-lg shadow-raised border border-line p-8">
            <LockIcon className="w-10 h-10 text-ink-3 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-4">Secret Corner access required</h2>
            <p className="text-ink-2 mb-6">
              This area is restricted to Secret Corner pass holders. There's no self-service
              request for it — if you need it, reach out to your administrator.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-green-ink text-white rounded-lg hover:opacity-90"
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // User is authorized - render children
  return <>{children}</>;
};

export default ProtectedRoute;
